import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { IAnamneseDetail } from 'src/app/models/anamnese.model';
import { IPatient } from 'src/app/models/patient.model';
import { IError } from 'src/app/models/error.model';
import { AnamneseService } from 'src/app/services/anamnese.service';
import { ModulesService } from 'src/app/services/modules.service';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { 
  faChevronLeft, 
  faCopy, 
  faCheck,
  faQrcode,
  faEnvelope,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-anamnese-create-v2',
  templateUrl: './anamnese-create.component.html',
  styleUrls: ['./anamnese-create.component.sass']
})
export class AnamneseCreateV2Component implements OnInit {
  loading = true;
  creatingAnamnese = false;
  
  // Query params
  patientId: string = '';
  patientName: string = '';
  patientEmail: string = '';
  patientPhoneNumber: string = '';
  reuseInfo: boolean = false;
  
  // Anamnese criada
  anamneseId: string = '';
  anamneseUrl: string = '';
  isAnswered: boolean = false;
  
  // Visualização (quando vem de ongoing)
  isViewMode: boolean = false;

  // Copy feedback
  showCopySuccess = false;

  // Icons
  faChevronLeft = faChevronLeft;
  faCopy = faCopy;
  faCheck = faCheck;
  faQrcode = faQrcode;
  faEnvelope = faEnvelope;
  faExternalLinkAlt = faExternalLinkAlt;

  // Módulo dinâmico
  viewModules$: Observable<{ moduleName: string; moduleNamePascalCase: string; moduleNameInSingular: string; }>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private anamneseService: AnamneseService,
    private store: Store<{ warningPopup: IError }>,
    public modulesService: ModulesService
  ) {
    this.viewModules$ = combineLatest({
      moduleName: this.modulesService.moduleName$,
      moduleNamePascalCase: this.modulesService.moduleNamePascalCase$,
      moduleNameInSingular: this.modulesService.moduleNameInSingular$
    });
  }

  ngOnInit(): void {
    // Verifica se é view mode (anamnese já existe)
    this.route.paramMap.subscribe(params => {
      const anamneseIdFromRoute = params.get('anamneseId');
      
      if (anamneseIdFromRoute) {
        // Modo visualização de anamnese existente
        this.isViewMode = true;
        this.anamneseId = anamneseIdFromRoute;
        this.loadExistingAnamnese();
      } else {
        // Modo criação de nova anamnese
        this.isViewMode = false;
        this.loadQueryParams();
        this.createAnamnese();
      }
    });
  }

  loadQueryParams(): void {
    this.patientId = this.route.snapshot.queryParams['patientId'] || '';
    this.patientName = this.route.snapshot.queryParams['patientName'] || '';
    this.patientEmail = this.route.snapshot.queryParams['patientEmail'] || '';
    this.patientPhoneNumber = this.route.snapshot.queryParams['patientPhoneNumber'] || '';
    this.reuseInfo = this.route.snapshot.queryParams['reuseInfo'] === 'true';
  }

  loadExistingAnamnese(): void {
    this.loading = true;
    this.patientId = this.route.snapshot.queryParams['patientId'] || '';
    this.patientName = this.route.snapshot.queryParams['patientName'] || '';
    
    this.anamneseService.getAnamneseById(this.anamneseId).then((anamnese) => {
      this.isAnswered = anamnese.is_answered || false;
      this.patientName = anamnese.patient?.full_name || this.patientName;
      this.patientEmail = anamnese.patient?.email || '';
      this.patientId = anamnese.patient_id || anamnese.patient?.id || this.patientId;
      this.formatAndSetUrl(anamnese.url_link || '');
      this.loading = false;
    }).catch((error: any) => {
      console.error('Erro ao carregar anamnese:', error);
      this.store.dispatch(setWarningText(this.getFormTypeErrorMessage('carregar')));
      this.loading = false;
      this.goBack();
    });
  }

  createAnamnese(): void {
    if (!this.patientId) {
      this.store.dispatch(setWarningText('Paciente não identificado.'));
      this.goBack();
      return;
    }

    this.loading = true;
    this.creatingAnamnese = true;

    const patient: Partial<IPatient> = {
      full_name: this.patientName,
      email: this.patientEmail,
      phone: this.patientPhoneNumber
    };

    const anamneseRequest: IAnamneseDetail = {
      patient_name: this.patientName,
      patient_email: this.patientEmail,
      patient_phone_number: this.patientPhoneNumber,
      reuse_info: this.reuseInfo,
      patient_id: this.patientId,
      patient: patient as IPatient
    };

    this.anamneseService.createAnamnese(anamneseRequest).subscribe({
      next: (response) => {
        this.anamneseId = response.id || '';
        this.formatAndSetUrl(response.url_link || '');
        this.isAnswered = false;
        this.loading = false;
        this.creatingAnamnese = false;
      },
      error: (error: any) => {
        console.error('Erro ao criar anamnese:', error);
        this.store.dispatch(setWarningText(this.getFormTypeErrorMessage('criar')));
        this.loading = false;
        this.creatingAnamnese = false;
      }
    });
  }

  formatAndSetUrl(urlLink: string): void {
    if (!urlLink) return;
    const baseUrl = window.location.href.split('/').slice(0, 3).join('/');
    this.anamneseUrl = urlLink.startsWith('http') ? urlLink : `${baseUrl}${urlLink}`;
  }

  copyLink(): void {
    if (this.anamneseUrl) {
      navigator.clipboard.writeText(this.anamneseUrl);
      this.showCopySuccess = true;
      setTimeout(() => {
        this.showCopySuccess = false;
      }, 3000);
    }
  }

  openLink(): void {
    if (this.anamneseUrl) {
      window.open(this.anamneseUrl, '_blank');
    }
  }

  goToAnswers(): void {
    if (this.anamneseId) {
      this.router.navigate(['main', 'v2', 'patients', 'anamnese', this.anamneseId]);
    }
  }

  goBack(): void {
    if (this.patientId) {
      this.router.navigate(['main', 'v2', 'patients', this.patientId, 'edit']);
    } else {
      this.router.navigate(['main', 'v2', 'patients']);
    }
  }

  // Helper para mensagens de erro dinâmicas conforme o módulo
  private getFormTypeErrorMessage(action: string): string {
    const formType = this.modulesService.moduleName === 'PACIENTES' ? 'anamnese' : 'formulário';
    if (action === 'carregar') {
      return `Erro ao carregar ${formType}.`;
    }
    return `Erro ao criar ${formType}. Tente novamente.`;
  }
}
